import TheForge from "./forgevtt";
import I18n from "./i18n";
import Utils from "./utils";

export default class UI {
    static async initI18n() {
        UI.lang = document.body.getAttribute("lang");
        this.i18n = this.i18n || new I18n(UI.lang);
        return this.i18n.init();
    }

    static initHandlebars() {
        Handlebars.registerHelper({
            bytesToHuman: Utils.bytesToHuman,
            divide: (a, b) => Number(a) / Number(b),
            multiply: (a, b) => Number(a) * Number(b),
            round: (a) => Math.round(a),
            toFixed: (a, d = 2) => a.toFixed(d),
            eq: function (a, b, options) {
                return options.fn ? options[a == b ? "fn" : "inverse"](this) : a == b;
            },
            ne: function (a, b, options) {
                return options.fn ? options[a != b ? "fn" : "inverse"](this) : a != b;
            },
            lt: function (a, b, options) {
                return options.fn ? options[a < b ? "fn" : "inverse"](this) : a < b;
            },
            gt: function (a, b, options) {
                return options.fn ? options[a > b ? "fn" : "inverse"](this) : a > b;
            },
            or: function (a, b, options) {
                return options.fn ? options[a || b ? "fn" : "inverse"](this) : a || b;
            },
            and: function (a, b, options) {
                return options.fn ? options[a && b ? "fn" : "inverse"](this) : a && b;
            },
            capitalize: (str) => Utils.capitalize(str),
            escapeAttr: (str) => (str || "").replace(/'/g, "&amp;").replace(/"/g, "&quot;"),
            t: (source, options) => this.i18n.hbsTranslate(source, options),
            concat: (str1, str2 = "", str3 = "") => {
                let strResult = "";
                for (const str of [str1, str2, str3]) {
                    // Skip options object always passed in as last argument by hbs
                    if (!Utils.isObject(str)) {
                        strResult += str;
                    }
                }
                return strResult;
            },
        });
    }
    static getScreenSize() {
        return {
            width: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
            height: window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
        };
    }
    static initHooks(element) {
        element = $(element || document);
        element.find(".form-control-copy-to-clipboard").click((ev) => TheForge.copyInputToClipboard(ev.currentTarget));
        element.find(".input-group-copy-to-clipboard").click((ev) => {
            TheForge.copyInputToClipboard($(ev.currentTarget).closest(".input-group").find("input")[0]);
        });
        element
            .find(".input-group-hide-until-change")
            .hide()
            .closest(".input-group")
            .find("input")
            .on("input", (ev) => {
                $(ev.currentTarget).closest(".input-group").find(".input-group-hide-until-change").show().off("input");
            });
    }
    static renderPartial(name, context = {}) {
        return $(Handlebars.partials[name](context));
    }
    static async renderVue(template, element, { data = {}, store = null, dependencies = [], renderData } = {}) {
        await Utils.loadDependencies(["vue", "i18n"].concat(dependencies));
        return new Vue({
            el: element,
            data,
            render: template ? (h) => h(this.VueComponents[template], renderData) : undefined,
            store,
            i18n: this.i18n.vueI18n,
        });
    }

    static async renderVueApps() {
        const components = $(".vue-render-app");
        for (let el of components.toArray()) {
            const id = el.id || "vueApp";
            const deps = el.getAttribute("dependencies") || undefined;
            // Can't change the class after render because the element won't exist anymore and there's no guarantee
            // that the Vue element itself won't be a comment/lazy-loaded.
            el.classList.remove("vue-render-app");
            el.classList.add("vue-rendered-app");
            this[id] = await UI.renderVue(null, el, { dependencies: deps && deps.split(" ") });
        }
    }

    /**
     * Displays an alert message on the UI.
     *
     * @param {string} type - The type of alert (e.g., 'success', 'error', 'warning').
     * @param {string} content - The content of the alert message.
     * @param {boolean} [dismiss=true] - Whether the alert can be dismissed by the user.
     * @param {number} [timeout=0] - The time in milliseconds after which the alert will automatically disappear. If 0, the alert will not disappear automatically.
     * @param {string} [appendTarget="#flash"] - The CSS selector of the element to which the alert will be appended.
     * @returns {jQuery} The jQuery object representing the alert element.
     */
    static alert(type, content, dismiss = true, timeout = 0, appendTarget = "#flash") {
        const $existingAlerts = $(`${appendTarget} .alert`);
        const duplicateAlert = $existingAlerts.toArray().find((existingAlert) => {
            const alertHtml = $(existingAlert).find(".alert-content").html();
            return alertHtml === content && $(existingAlert).hasClass(`alert-${type}`);
        });
        if (duplicateAlert) {
            const $duplicateAlert = $(duplicateAlert);
            $duplicateAlert.appendTo(appendTarget);
            const $badge = $duplicateAlert.find(".alert-count");
            if ($badge.length === 1) {
                const currentCount = parseInt($badge.text(), 10) || 1;
                const nextCount = currentCount + 1;
                $badge.text(nextCount).css("display", "inline").attr("aria-label", `${nextCount} alerts`);
            }
            return $duplicateAlert;
        }
        const $alert = $(HandlebarsTemplates.alert({ type, content, dismiss }));
        $(appendTarget).append($alert);
        if (timeout > 0) {
            setTimeout(() => $alert.remove(), timeout);
        }
        return $alert;
    }

    static pushAlert(type, content, dismiss = true, timeout = 0) {
        let alerts = JSON.parse(localStorage.getItem("alerts") || "[]");
        alerts.push({ type, content, dismiss, timeout });
        localStorage.setItem("alerts", JSON.stringify(alerts));
    }

    static renderPushedAlerts() {
        let alerts = JSON.parse(localStorage.getItem("alerts") || "[]");
        alerts.forEach((a) => UI.alert(a.type, a.content, a.dismiss, a.timeout));
        localStorage.removeItem("alerts");
    }

    static error(content) {
        UI.alert("danger", content, true);
    }
    static warn(content) {
        UI.alert("warning", content, true, 20000);
    }
    static info(content) {
        UI.alert("info", content, true, 10000);
    }
    static success(content) {
        UI.alert("success", content, false, 10000);
    }

    static createModal(title, body, footer, buttons, options = {}) {
        const modal = $(HandlebarsTemplates.modal({ title, body, footer, buttons, options }));
        modal.on("hidden.bs.modal", () => modal.remove());
        $("body").append(modal);
        return modal;
    }

    static async query(title, query, { buttons, yes, no, modal_classes, dialog_classes } = {}) {
        let result = "cancel";

        if (!buttons) {
            buttons = [];
            if (yes) {
                buttons.push({
                    type: "success",
                    id: "query-modal-yes",
                    label: yes,
                    response: "yes",
                });
            }
            if (no) {
                buttons.push({
                    type: "danger",
                    id: "query-modal-no",
                    label: no,
                    response: "yes",
                });
            }
        }

        const modal = this.createModal(title, query, "", buttons, {
            static: true,
            id: "query-modal",
            close: true,
            modal_classes: modal_classes || "fade",
            dialog_classes,
        });
        for (let button of buttons) {
            if (!button.id) {
                continue;
            }
            modal.find(`#${button.id}`).on("click", () => {
                result = button.response || button.id;
                modal.modal("hide");
            });
        }
        modal.modal("show");
        return new Promise((resolve) => {
            modal.on("hide.bs.modal", () => resolve(result));
        });
    }

    /**
     * Create a Progress Bar
     * @param {jquery} div          Parent div to put the progress bar into
     * @param {Array|Object} bars   Bar options or array of options
     */
    static createProgressBar(div, bars = { stripped: true, animated: true }) {
        bars = bars instanceof Array ? bars : [bars];
        const progress = $(HandlebarsTemplates.progress({ bars }));
        progress.bars = progress.find(".progress-bar");
        progress.label = progress.find(".progress-label");
        progress.setProgress = (value, index = 0) =>
            progress.bars
                .eq(index)
                .attr("aria-valuenow", value)
                .css({ width: `${value}%` });
        progress.setLabel = (value) => progress.label.text(value);
        progress.addClass("bg-secondary");
        div.append(progress);
        return progress;
    }
    static createCard(title, body, header = null, footer = null, img_top = null, img_bottom = null, options = {}) {
        return $(this.renderPartial("card", { title, body, header, footer, img_top, img_bottom, options }));
    }

    static initTurnstile() {
        for (const el of $(".render-cf-turnstile").toArray()) {
            const siteKey = el.dataset.sitekey;
            if (!siteKey) {
                continue;
            }
            const action = el.dataset.action || undefined;
            const darkTheme = $(".forge-theme-toggle input").prop("checked");
            const widgetId = window.turnstile.render(el, {
                sitekey: siteKey,
                action,
                language: UI.lang,
                theme: darkTheme ? "dark" : "light",
                callback(token) {
                    el.dataset.cfTurnstileResponse = token;
                },
            });
            el.dataset.cfTurnstileWidgetId = widgetId;
        }
    }
}
